import { useRef, useState } from 'react';
import { FileUp } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { MonthInput } from '@/components/ui/month-input';
import { useAdminTenants } from '@/hooks/use-admin';
import { useUploadStatement } from '@/hooks/use-admin-statements';
import { toast } from '@/stores/toast-store';

export function AdminStatementsUploadPage() {
  const { data: tenantsData, isLoading: tenantsLoading } = useAdminTenants({ perPage: 200 });
  const tenants = tenantsData?.data ?? [];

  const [tenantId, setTenantId] = useState('');
  const [monthInput, setMonthInput] = useState(''); // "YYYY-MM"
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadStatement = useUploadStatement();

  const canUpload = Boolean(tenantId && monthInput && file) && !uploadStatement.isPending;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (selected.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      e.target.value = '';
      return;
    }
    setFile(selected);
  };

  const handleUpload = async () => {
    if (!canUpload || !file) return;
    const [year, month] = monthInput.split('-').map((n) => parseInt(n, 10));

    try {
      await uploadStatement.mutateAsync({ tenantId, month, year, file });
      toast.success('File uploaded successfully');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      const message = (err as { error?: { message?: string } })?.error?.message || 'Upload failed';
      toast.error(message);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Statements Upload" description="Upload a pre-generated Card Statement PDF for a business." />

      <div className="max-w-xl space-y-4 rounded-xl border border-border bg-white p-6">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Business</label>
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            disabled={tenantsLoading}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          >
            <option value="">{tenantsLoading ? 'Loading…' : 'Select a business'}</option>
            {tenants.map((t: { id: string; name: string }) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Statement Month</label>
          <MonthInput
            value={monthInput}
            onChange={(e) => setMonthInput(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Statement PDF</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={handleFileSelect}
            className="w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary hover:file:bg-primary/20"
          />
        </div>

        <Button variant="primary" disabled={!canUpload} onClick={handleUpload}>
          <FileUp className="h-3.5 w-3.5" />
          {uploadStatement.isPending ? 'Uploading…' : 'Upload'}
        </Button>
      </div>
    </div>
  );
}
